import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminAuth } from '../context/AdminAuthContext'

const benefits = [
  { icon: 'fa-building', title: 'Gestão centralizada', text: 'Clientes, revendas e acessos em um só lugar.' },
  { icon: 'fa-chart-line', title: 'Visão em tempo real', text: 'Indicadores claros para decisões mais rápidas.' },
  { icon: 'fa-shield-halved', title: 'Operação protegida', text: 'Ambiente exclusivo para administradores autorizados.' },
]

export default function Login() {
  const { login, getApiErrorMessage } = useAdminAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(event) {
    event.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email.trim(), password)
      navigate('/')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível entrar no painel.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="mp-login min-h-[100dvh] lg:grid lg:grid-cols-[minmax(0,1.12fr)_minmax(430px,.88fr)]">
      <section className="mp-login-showcase relative hidden overflow-hidden px-10 py-9 text-white lg:flex lg:flex-col xl:px-16 xl:py-12">
        <div className="mp-login-grid" aria-hidden="true" />
        <div className="mp-login-orb mp-login-orb-one" aria-hidden="true" />
        <div className="mp-login-orb mp-login-orb-two" aria-hidden="true" />

        <header className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <span className="grid h-12 w-12 place-items-center rounded-2xl border border-white/20 bg-white/10 text-xl shadow-xl backdrop-blur-xl">
              <i className="fas fa-shield-halved" aria-hidden="true" />
            </span>
            <div>
              <p className="text-[11px] font-extrabold uppercase tracking-[.22em] text-cyan-200">MAM Control</p>
              <p className="text-xl font-bold tracking-tight">M-Panel</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-xs font-semibold text-blue-50 backdrop-blur-xl">
            <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_5px_rgba(52,211,153,.13)]" />
            Ambiente seguro
          </span>
        </header>

        <div className="relative z-10 my-auto max-w-2xl py-12">
          <span className="inline-flex items-center gap-2 rounded-full border border-cyan-200/20 bg-cyan-200/10 px-4 py-2 text-xs font-bold uppercase tracking-[.16em] text-cyan-100">
            <i className="fas fa-wand-magic-sparkles" aria-hidden="true" />
            Centro de comando
          </span>
          <h1 className="mt-7 max-w-xl text-5xl font-extrabold leading-[1.06] tracking-[-.045em] xl:text-6xl">
            Controle total.
            <span className="mp-login-gradient-text block">Operação simples.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-7 text-blue-100/85 xl:text-lg">
            Uma visão completa do ecossistema MAM Control para administrar clientes, acompanhar a operação e crescer com segurança.
          </p>

          <div className="mt-10 grid gap-3 xl:grid-cols-3">
            {benefits.map((item) => (
              <article key={item.title} className="mp-login-benefit rounded-2xl border border-white/10 p-4 backdrop-blur-xl">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/10 text-cyan-200">
                  <i className={`fas ${item.icon}`} aria-hidden="true" />
                </span>
                <h2 className="mt-4 text-sm font-bold text-white">{item.title}</h2>
                <p className="mt-1.5 text-xs leading-5 text-blue-100/70">{item.text}</p>
              </article>
            ))}
          </div>
        </div>

        <footer className="relative z-10 flex items-center justify-between text-xs text-blue-200/70">
          <span>© {new Date().getFullYear()} MAM Control</span>
          <span className="flex items-center gap-2"><i className="fas fa-lock" /> Conexão protegida</span>
        </footer>
      </section>

      <section className="mp-login-form-side flex min-h-[100dvh] items-center justify-center px-5 py-8 sm:px-10 lg:min-h-0 xl:px-16">
        <div className="w-full max-w-[460px]">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-brand-dark to-brand-primary text-white shadow-lg shadow-blue-900/20">
              <i className="fas fa-shield-halved" aria-hidden="true" />
            </span>
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[.2em] text-brand-primary">MAM Control</p>
              <p className="font-bold text-brand-deep">M-Panel</p>
            </div>
          </div>

          <div className="mp-login-card rounded-[2rem] border border-white/80 bg-white/80 p-6 shadow-[0_30px_80px_rgba(7,39,90,.14)] backdrop-blur-xl sm:p-9">
            <div className="mb-8">
              <span className="mb-5 grid h-12 w-12 place-items-center rounded-2xl bg-blue-50 text-lg text-brand-primary ring-1 ring-blue-100">
                <i className="fas fa-fingerprint" aria-hidden="true" />
              </span>
              <p className="text-xs font-extrabold uppercase tracking-[.18em] text-brand-primary">Área administrativa</p>
              <h2 className="mt-2 text-3xl font-extrabold tracking-[-.035em] text-brand-deep">Bem-vindo de volta</h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">Use suas credenciais para acessar o painel de gestão.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-slate-700">E-mail</span>
                <span className="mp-login-field flex items-center rounded-2xl border bg-white px-4">
                  <i className="fas fa-envelope w-5 text-sm text-slate-400" aria-hidden="true" />
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="admin@mamcontrol.com"
                    required
                    autoComplete="username"
                    autoFocus
                    className="min-h-14 w-full border-0 bg-transparent px-3 text-sm text-slate-800 outline-none placeholder:text-slate-400"
                  />
                </span>
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-bold text-slate-700">Senha</span>
                <span className="mp-login-field flex items-center rounded-2xl border bg-white px-4">
                  <i className="fas fa-lock w-5 text-sm text-slate-400" aria-hidden="true" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Digite sua senha"
                    required
                    autoComplete="current-password"
                    className="min-h-14 min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-slate-800 outline-none placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-slate-400 transition hover:bg-blue-50 hover:text-brand-primary"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
                  </button>
                </span>
              </label>

              {error && (
                <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800" role="alert">
                  <i className="fas fa-circle-exclamation mt-1 text-red-500" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              <button type="submit" disabled={loading} className="mp-login-submit flex min-h-14 w-full items-center justify-center gap-2.5 rounded-2xl px-5 text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-70">
                <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-arrow-right-to-bracket'}`} aria-hidden="true" />
                {loading ? 'Entrando…' : 'Entrar no painel'}
              </button>
            </form>

            <div className="mt-7 flex items-center justify-center gap-2 border-t border-slate-100 pt-6 text-center text-xs text-slate-400">
              <i className="fas fa-circle-check text-emerald-500" aria-hidden="true" />
              Acesso monitorado e restrito
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
